'use client';
import { getSupabaseClient } from '../supabase';

class CommentaryService {
  // Convert database row to camelCase
  _toCamelCase(row) {
    if (!row) return null;

    return {
      id: row?.id,
      scope: row?.scope,
      bookCode: row?.book_code,
      chapterNumber: row?.chapter_number,
      verseNumber: row?.verse_number,
      title: row?.title,
      content: row?.content,
      status: row?.status,
      createdBy: row?.created_by,
      updatedBy: row?.updated_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    };
  }

  // Convert camelCase to database format
  _toSnakeCase(data) {
    return {
      scope: data?.scope,
      book_code: data?.bookCode,
      chapter_number: data?.chapterNumber,
      verse_number: data?.verseNumber,
      title: data?.title,
      content: data?.content,
      status: data?.status,
      created_by: data?.createdBy,
      updated_by: data?.updatedBy
    };
  }

  // Get commentary for a specific verse with fallback to chapter/book
  async getCommentaryForVerse(bookCode, chapterNumber, verseNumber) {
    try {
      const supabase = getSupabaseClient();

      // Safety check for undefined client
      if (!supabase) {
        console.warn('Supabase client not initialized');
        return null;
      }

      // First try to find verse-specific commentary
      let { data, error } = await supabase?.from('commentary_entries')?.select('*')?.eq('book_code', bookCode)?.eq('chapter_number', chapterNumber)?.eq('verse_number', verseNumber)?.eq('scope', 'verse')?.eq('status', 'published')?.maybeSingle();

      if (error) throw error;

      if (data) {
        return {
          type: 'verse',
          commentary: this._toCamelCase(data)
        };
      }

      // If no verse commentary, try chapter-level
      ({ data, error } = await supabase?.from('commentary_entries')?.select('*')?.eq('book_code', bookCode)?.eq('chapter_number', chapterNumber)?.eq('scope', 'chapter')?.eq('status', 'published')?.maybeSingle());

      if (error) throw error;

      if (data) {
        return {
          type: 'chapter',
          commentary: this._toCamelCase(data)
        };
      }

      // If no chapter commentary, try book-level
      ({ data, error } = await supabase?.from('commentary_entries')?.select('*')?.eq('book_code', bookCode)?.eq('scope', 'book')?.eq('status', 'published')?.maybeSingle());

      if (error) throw error;

      if (data) {
        return {
          type: 'book',
          commentary: this._toCamelCase(data)
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching commentary:', error);
      throw error;
    }
  }

  // UPDATED METHOD: Get commentary using verse ID from bible_admin_verses
  async getCommentaryByVerseId(verseId) {
    try {
      const supabase = getSupabaseClient();

      // Safety check for undefined client
      if (!supabase) {
        console.warn('Supabase client not initialized');
        return null;
      }

      // First, get the verse details INCLUDING the chapter and book relationships
      const { data: verseData, error: verseError } = await supabase
        ?.from('bible_admin_verses')
        ?.select(`
          verse_number,
          bible_admin_chapters!inner (
            chapter_number,
            bible_admin_books!inner (
              book_code
            )
          )
        `)
        ?.eq('id', verseId)
        ?.single();

      if (verseError) {
        console.error('Error fetching verse details for commentary:', verseError);
        return null;
      }

      if (!verseData || !verseData?.bible_admin_chapters) {
        console.warn('Verse data or chapter relationship not found for ID:', verseId);
        return null;
      }

      const bookCode = verseData?.bible_admin_chapters?.bible_admin_books?.book_code;
      const chapterNumber = verseData?.bible_admin_chapters?.chapter_number;
      const verseNumber = verseData?.verse_number;

      if (!bookCode || !chapterNumber || !verseNumber) {
        console.warn('Missing required data for commentary lookup:', { bookCode, chapterNumber, verseNumber });
        return null;
      }

      // Now use the existing method to get commentary
      return await this.getCommentaryForVerse(bookCode, chapterNumber, verseNumber);
    } catch (error) {
      console.error('Error fetching commentary by verse ID:', error);
      return null;
    }
  }

  // Get Treasury of Scripture Knowledge for a verse
  async getTskForVerse(bookCode, chapterNumber, verseNumber) {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase?.from('treasury_scripture_knowledge')?.select('*')?.eq('book_code', bookCode)?.eq('chapter_number', chapterNumber)?.eq('verse_number', verseNumber)?.maybeSingle();

      if (error) throw error;

      return data ? {
        id: data?.id,
        bookCode: data?.book_code,
        chapterNumber: data?.chapter_number,
        verseNumber: data?.verse_number,
        crossReferences: data?.cross_references,
        content: data?.content,
        createdAt: data?.created_at
      } : null;
    } catch (error) {
      console.error('Error fetching TSK:', error);
      throw error;
    }
  }

  // Admin functions for managing commentary

  // Get all commentary entries with filters
  async getAllCommentary(filters = {}) {
    try {
      const supabase = getSupabaseClient();
      let query = supabase?.from('commentary_entries')?.select(`
          *,
          created_by_profile:user_profiles!commentary_entries_created_by_fkey(full_name),
          updated_by_profile:user_profiles!commentary_entries_updated_by_fkey(full_name)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.bookCode) {
        query = query?.eq('book_code', filters?.bookCode);
      }
      if (filters?.chapterNumber) {
        query = query?.eq('chapter_number', filters?.chapterNumber);
      }
      if (filters?.scope) {
        query = query?.eq('scope', filters?.scope);
      }
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        ...this._toCamelCase(row),
        createdByName: row?.created_by_profile?.full_name,
        updatedByName: row?.updated_by_profile?.full_name
      })) || [];
    } catch (error) {
      console.error('Error fetching all commentary:', error);
      throw error;
    }
  }

  // Get commentary entry by ID
  async getCommentaryById(id) {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase?.from('commentary_entries')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return this._toCamelCase(data);
    } catch (error) {
      console.error('Error fetching commentary by ID:', error);
      throw error;
    }
  }

  // NEW METHOD: Simplified commentary lookup using book/chapter/verse data
  async getCommentaryByLocation(bookCode, chapterNumber, verseNumber) {
    try {
      const supabase = getSupabaseClient();

      if (!supabase) {
        console.warn('Supabase client not initialized');
        return null;
      }

      // Direct lookup using the location data
      return await this.getCommentaryForVerse(bookCode, chapterNumber, verseNumber);
    } catch (error) {
      console.error('Error fetching commentary by location:', error);
      return null;
    }
  }

  // Create new commentary entry
  async createCommentary(commentaryData) {
    try {
      const supabase = getSupabaseClient();
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const dbData = {
        ...this._toSnakeCase(commentaryData),
        created_by: user?.id,
        updated_by: user?.id
      };

      const { data, error } = await supabase?.from('commentary_entries')?.insert(dbData)?.select()?.single();

      if (error) throw error;

      return this._toCamelCase(data);
    } catch (error) {
      console.error('Error creating commentary:', error);
      throw error;
    }
  }

  // Update commentary entry
  async updateCommentary(id, commentaryData) {
    try {
      const supabase = getSupabaseClient();
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const dbData = {
        ...this._toSnakeCase(commentaryData),
        updated_by: user?.id
      };

      const { data, error } = await supabase?.from('commentary_entries')?.update(dbData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return this._toCamelCase(data);
    } catch (error) {
      console.error('Error updating commentary:', error);
      throw error;
    }
  }

  // Delete commentary entry
  async deleteCommentary(id) {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase?.from('commentary_entries')?.delete()?.eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting commentary:', error);
      throw error;
    }
  }

  // Check if user is admin
  async isAdmin() {
    try {
      const supabase = getSupabaseClient();

      // Safety check for undefined client
      if (!supabase) {
        console.warn('Supabase client not initialized for admin check');
        return false;
      }

      // Call RPC and safely handle response
      const response = await supabase?.rpc('is_admin_user');

      // Ensure response exists before destructuring
      if (!response) {
        console.warn('No response from admin check RPC');
        return false;
      }

      const { data, error } = response;

      if (error) {
        console.error('Admin check RPC error:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Publish/unpublish commentary
  async updateCommentaryStatus(id, status) {
    try {
      const supabase = getSupabaseClient();
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('commentary_entries')?.update({
          status,
          updated_by: user?.id
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return this._toCamelCase(data);
    } catch (error) {
      console.error('Error updating commentary status:', error);
      throw error;
    }
  }
}

export const commentaryService = new CommentaryService();
